import { ReactNode } from "react";

interface ContactSupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ContactOption {
  id: string;
  icon: ReactNode;
  label: string;
  description: string;
  action: () => void;
}

// Farcaster logo SVG
const FarcasterIcon = () => (
  <svg viewBox="0 0 1000 1000" fill="currentColor" className="w-5 h-5">
    <path d="M257.778 155.556h484.444v688.889h-71.111V528.889c0-39.111-31.778-70.889-71.111-70.889H400c-39.111 0-71.111 31.778-71.111 70.889v315.556h-71.111V155.556z" />
    <path d="M128.889 253.333l28.889-97.778h684.444l28.889 97.778v53.334H128.889v-53.334z" />
    <path d="M186.667 306.667h626.666v53.333c0 39.112-31.777 71.111-71.111 71.111h-484.444c-39.111 0-71.111-31.999-71.111-71.111v-53.333z" />
  </svg>
);

export function ContactSupportModal({
  isOpen,
  onClose,
}: ContactSupportModalProps) {
  if (!isOpen) return null;

  const contactOptions: ContactOption[] = [
    {
      id: "email",
      icon: (
        <span className="material-symbols-outlined text-primary text-xl">
          mail
        </span>
      ),
      label: "Email",
      description: "Send us an email and we'll respond within 24 hours",
      action: () => {
        window.open("mailto:support@dasher.fit", "_blank");
      },
    },
    {
      id: "farcaster",
      icon: <FarcasterIcon />,
      label: "Farcaster",
      description: "Message us on Farcaster",
      action: () => {
        window.open("https://warpcast.com/keccak0256", "_blank");
      },
    },
    {
      id: "telegram",
      icon: (
        <span className="material-symbols-outlined text-primary text-xl">
          send
        </span>
      ),
      label: "Telegram",
      description: "Chat with us on Telegram",
      action: () => {
        window.open("https://t.me/+7qTHVrqjHlNhMmRl", "_blank");
      },
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative flex w-full max-w-md flex-col bg-background-light dark:bg-background-dark rounded-3xl p-6 mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
        >
          <span className="material-symbols-outlined text-gray-600 dark:text-gray-400">
            close
          </span>
        </button>

        {/* Header */}
        <div className="flex flex-col items-center gap-4 mb-6">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/20">
            <span className="material-symbols-outlined text-primary text-3xl">
              support_agent
            </span>
          </div>
          <div className="text-center">
            <h2 className="text-gray-900 dark:text-white text-2xl font-bold mb-2">
              Contact Us
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Choose your preferred way to reach our support team
            </p>
          </div>
        </div>

        {/* Contact Options */}
        <div className="flex flex-col gap-3 mb-6">
          {contactOptions.map((option) => (
            <button
              key={option.id}
              onClick={option.action}
              className="flex items-center gap-4 p-4 rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-black/20 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors text-left"
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/20 shrink-0 text-primary">
                {option.icon}
              </div>
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-gray-900 dark:text-white font-semibold">
                  {option.label}
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-sm leading-tight">
                  {option.description}
                </span>
              </div>
              <span className="material-symbols-outlined text-gray-400 dark:text-gray-500 ml-auto shrink-0">
                chevron_right
              </span>
            </button>
          ))}
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="flex items-center justify-center h-12 rounded-xl bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white font-bold hover:bg-gray-200 dark:hover:bg-white/20 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
}
